import { useMemo } from 'react';
import { ZodObject, type ZodTypeAny } from 'zod';
import type { FieldConfig } from '@/components/blocks/forms';

type GenericZodObject = ZodObject<Record<string, ZodTypeAny>>;

interface UseUnifiedFormConfig {
  schema: GenericZodObject;
  fields: readonly FieldConfig[];
  fieldOverrides?: Partial<Record<string, Partial<FieldConfig>>>;
  fieldOrder?: string[];
  excludeFields?: string[];
}

/**
 * Hook to configure form fields with ordering, filtering, and overrides.
 * Returns the processed fields array ready for use with EntityFormFields.
 */
export function useUnifiedForm({
  fields: baseFields,
  fieldOverrides = {},
  fieldOrder = [],
  excludeFields = [],
}: UseUnifiedFormConfig) {
  const fields = useMemo(() => {
    let extractedFields = baseFields;

    // Filter out excluded fields
    if (excludeFields.length > 0) {
      extractedFields = extractedFields.filter((field) => !excludeFields.includes(field.key));
    }

    // Apply field overrides
    const fieldsWithOverrides = extractedFields.map((field) => {
      const override = fieldOverrides[field.key];
      if (override) {
        return { ...field, ...override };
      }
      return field;
    });

    // Apply custom ordering if specified
    if (fieldOrder.length > 0) {
      const orderedFields: typeof fieldsWithOverrides = [];
      const fieldMap = new Map(fieldsWithOverrides.map((f) => [f.key, f]));

      // Add fields in specified order
      fieldOrder.forEach((key) => {
        const field = fieldMap.get(key);
        if (field) {
          orderedFields.push(field);
          fieldMap.delete(key as string);
        }
      });

      // Add remaining fields that weren't in the order
      fieldMap.forEach((field) => {
        orderedFields.push(field);
      });

      return orderedFields;
    }

    return fieldsWithOverrides;
  }, [baseFields, fieldOverrides, fieldOrder, excludeFields]);

  return { fields };
}
